import type { ViewNode } from './graphModel';

type KindColours = {
  /** The minimap fill: the hex value of `compact`'s light-mode colour (the minimap takes no classes). */
  minimap: string;
  /** The minimap fill in dark mode: the hex value of `compact`'s dark-mode colour. */
  minimapDark: string;
  /** The solid fill of a card drawn as a block when zoomed out. */
  compact: string;
  /** The fill of the small swatch beside the kind's filter row. */
  swatch: string;
};

/**
 * One colour per node kind, shared by the zoomed-out cards, the minimap, and the node type filter's swatches, so
 * the filter reads as the legend of both. Class names are written out in full so Tailwind finds them.
 */
export const KIND_PALETTE: Record<ViewNode['kind'], KindColours> = {
  engine: { minimap: '#6366f1', minimapDark: '#6366f1', compact: 'bg-indigo-500', swatch: 'bg-indigo-500' },
  aircraft: { minimap: '#0ea5e9', minimapDark: '#0ea5e9', compact: 'bg-sky-500', swatch: 'bg-sky-500' },
  part: { minimap: '#10b981', minimapDark: '#10b981', compact: 'bg-emerald-500', swatch: 'bg-emerald-500' },
  reference: { minimap: '#f59e0b', minimapDark: '#f59e0b', compact: 'bg-amber-500', swatch: 'bg-amber-500' },
  document: {
    minimap: '#a1a1aa',
    minimapDark: '#71717a',
    compact: 'bg-zinc-400 dark:bg-zinc-500',
    swatch: 'bg-zinc-400 dark:bg-zinc-500',
  },
  event: { minimap: '#64748b', minimapDark: '#64748b', compact: 'bg-slate-500', swatch: 'bg-slate-500' },
  ipc: { minimap: '#8b5cf6', minimapDark: '#8b5cf6', compact: 'bg-violet-500', swatch: 'bg-violet-500' },
  group: {
    minimap: '#d4d4d8',
    minimapDark: '#52525b',
    compact: 'bg-zinc-300 dark:bg-zinc-600',
    swatch: 'bg-zinc-300 dark:bg-zinc-600',
  },
  project: { minimap: '#6366f1', minimapDark: '#6366f1', compact: 'bg-indigo-500', swatch: 'bg-indigo-500' },
};

/** A node kind added after this release. */
export const FALLBACK_COLOURS: KindColours = {
  minimap: '#9ca3af',
  minimapDark: '#9ca3af',
  compact: 'bg-gray-400',
  swatch: 'bg-gray-400',
};

/** The colours of a kind, or `FALLBACK_COLOURS` for a kind without an entry. */
export const kindColours = (kind: string): KindColours =>
  KIND_PALETTE[kind as ViewNode['kind']] ?? FALLBACK_COLOURS;

/**
 * A kind's minimap fill for both modes, as CSS `light-dark()`: it follows the `color-scheme` the canvas sets from
 * the app's `dark` class, so the minimap matches the compact cards in either mode.
 */
export const minimapFill = (kind: string): string => {
  const { minimap, minimapDark } = kindColours(kind);
  return minimap === minimapDark ? minimap : `light-dark(${minimap}, ${minimapDark})`;
};
